import React from 'react';
import { CalendarDays, CheckCircle, FileText, RefreshCw, Rocket, XCircle } from 'lucide-react';
import type { CalendarStats } from '@/types/contentPlanner';

export interface SummaryStripProps {
  stats: CalendarStats;
  /** Status-filter value currently applied to the list (e.g. 'all', 'in_progress'). */
  activeFilter?: string;
  /** Click a card to filter the list to that status group. Clicking the active card resets to 'all'. */
  onCardClick?: (filterValue: string) => void;
}

// `filter` is the status value sent to the calendar endpoint when the card is
// clicked; it maps to the SAME group the count is computed from (backend
// get_calendar_items), so the filtered list matches the card number.
const ITEMS = [
  { key: 'total', label: 'Total Posts', icon: FileText, color: 'var(--color-ink)', filter: 'all' },
  { key: 'generating', label: 'In Progress', icon: RefreshCw, color: 'var(--color-teal-dark)', filter: 'in_progress' },
  { key: 'draft', label: 'Ready', icon: CheckCircle, color: 'var(--color-coral-dark)', filter: 'ready' },
  { key: 'scheduled', label: 'Scheduled', icon: CalendarDays, color: 'var(--color-amber-dark)', filter: 'scheduled' },
  { key: 'published', label: 'Published', icon: Rocket, color: 'var(--color-teal-dark)', filter: 'published' },
  { key: 'failed', label: 'Failed', icon: XCircle, color: 'var(--color-destructive)', filter: 'failed' },
] as const;

export const SummaryStrip: React.FC<SummaryStripProps> = ({ stats, activeFilter = 'all', onCardClick }) => {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
        gap: 10,
        padding: '12px 28px',
        background: 'var(--color-warm-bg)',
        borderBottom: '1px solid var(--color-warm-border)',
      }}
    >
      {ITEMS.map((item) => {
        const Icon = item.icon;
        const value = stats[item.key] ?? 0;
        const isActive = activeFilter === item.filter && item.filter !== 'all';
        const clickable = !!onCardClick;
        return (
          <button
            key={item.key}
            type="button"
            onClick={
              clickable
                ? () => onCardClick(isActive ? 'all' : item.filter)
                : undefined
            }
            aria-pressed={isActive}
            title={
              clickable
                ? isActive
                  ? `Clear ${item.label} filter`
                  : `Show ${item.label}`
                : undefined
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              minWidth: 0,
              padding: '10px 12px',
              borderRadius: 8,
              textAlign: 'left',
              font: 'inherit',
              background: isActive ? 'var(--color-coral-light)' : 'var(--color-paper)',
              border: `1px solid ${isActive ? 'var(--color-coral)' : 'var(--color-warm-border)'}`,
              cursor: clickable ? 'pointer' : 'default',
              transition: 'border-color 0.12s, background 0.12s',
            }}
          >
            <div
              style={{
                display: 'grid',
                placeItems: 'center',
                width: 28,
                height: 28,
                borderRadius: 7,
                background: 'var(--color-warm-bg)',
                color: item.color,
              }}
            >
              <Icon
                size={15}
                strokeWidth={1.75}
                className={item.key === 'generating' && value > 0 ? 'animate-spin' : undefined}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                className="mono"
                style={{
                  color: 'var(--color-ink)',
                  fontSize: 17,
                  fontWeight: 700,
                  lineHeight: 1.1,
                }}
              >
                {value.toLocaleString()}
              </div>
              <div
                style={{
                  color: 'var(--color-ink-3)',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.label}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default SummaryStrip;
